import {
    blackFridayStyles,
    cyberMondayStyles,
    defaultBadgeStyles,
    discountBadgeStyles,
    eccoClubSaleStyles,
} from "@components/ui/product-badges/styles";
import { CommonCMS } from "@lib/constants/contentful";

export enum ProductBadgeVariant {
    DEFAULT = "default",
    DISCOUNT = "discount",
    BLACK_FRIDAY = "black_friday",
    CYBER_MONDAY = "cyber_monday",
    ECCO_CLUB_SALE = "ecco_club_sale",
}

export const badgesStyles = {
    [ProductBadgeVariant.DEFAULT]: defaultBadgeStyles,
    [ProductBadgeVariant.DISCOUNT]: discountBadgeStyles,
    [ProductBadgeVariant.BLACK_FRIDAY]: blackFridayStyles,
    [ProductBadgeVariant.CYBER_MONDAY]: cyberMondayStyles,
    [ProductBadgeVariant.ECCO_CLUB_SALE]: eccoClubSaleStyles,
};

export interface Badge {
    name: string;
    type: ProductBadgeVariant;
    testId: string;
}

interface ScheduledBadge {
    value: string;
    start?: string;
    end?: string;
}

// CustomBadge1 comes as a plain string ("NEW"), scheduled badges come as JSON
// like {"value":"ECCO CLUB","start":"...","end":"..."}
const toScheduledBadge = (badge: unknown): ScheduledBadge | undefined => {
    if (typeof badge === "string") {
        if (!badge.trim().startsWith("{")) return { value: badge };
        try {
            return JSON.parse(badge);
        } catch {
            return undefined;
        }
    }
    if (typeof badge === "object" && badge !== null) return badge as ScheduledBadge;
    return undefined;
};

// Keeps only badges that are currently active: no dates means always show,
// otherwise the current time has to be after start and/or before end.
export const getActiveCustomBadges = (badges: unknown[]): string[] => {
    const now = new Date();
    return badges
        .map(toScheduledBadge)
        .filter((badge): badge is ScheduledBadge => Boolean(badge?.value))
        .filter(
            (badge) =>
                (!badge.start || now >= new Date(badge.start)) &&
                (!badge.end || now <= new Date(badge.end))
        )
        .map((badge) => badge.value);
};

const mapProductBadgeVariant = (
    badge: string,
    microcopies: Record<string, string>
): ProductBadgeVariant => {
    const badgeLower = badge.toLowerCase();
    if (badgeLower === microcopies[CommonCMS.salesDays.blackFriday]?.toLowerCase()) {
        return ProductBadgeVariant.BLACK_FRIDAY;
    }
    if (badgeLower === microcopies[CommonCMS.salesDays.cyberMonday]?.toLowerCase()) {
        return ProductBadgeVariant.CYBER_MONDAY;
    }
    return ProductBadgeVariant.DEFAULT;
};

export const formatBadges = (
    customBadges: string[],
    microcopies: Record<string, string>
): Badge[] => {
    if (!customBadges?.length) return [];

    const priorities = {
        [microcopies[CommonCMS.salesDays.blackFriday]?.toLowerCase()]: 1,
        [microcopies[CommonCMS.salesDays.cyberMonday]?.toLowerCase()]: 1,
    };

    const sortedBadges = [...customBadges].sort(
        (a, b) =>
            (priorities[a.toLowerCase()] || Infinity) - (priorities[b.toLowerCase()] || Infinity)
    );

    return sortedBadges.map((badge) => ({
        name: badge,
        type: mapProductBadgeVariant(badge, microcopies),
        testId: mapProductBadgeVariant(badge, microcopies),
    }));
};
